import { useEffect, useState } from "react";
import { X, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const SITE_STATUS = {
  attivo: { label: "Attivo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  sospeso: { label: "Sospeso", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  chiuso: { label: "Chiuso", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const empty = { name: "", address: "", street: "", house_number: "", postal_code: "", city: "", province: "", country: "Italia", client_name: "", status: "attivo", start_date: "", end_date: "", notes: "", keywords: "", contacts: [] };

export const SiteFormModal = ({ site, onClose, onSaved }) => {
  const [form, setForm] = useState(site ? {
    ...empty,
    ...site,
    street: site.street || site.address || "",
    house_number: site.house_number || "",
    postal_code: site.postal_code || "",
    city: site.city || "",
    province: site.province || "",
    country: site.country || "Italia",
    keywords: (site.keywords || []).join(", "),
    start_date: site.start_date || "",
    end_date: site.end_date || ""
  } : empty);
  const [saving, setSaving] = useState(false);
  const [directoryContacts, setDirectoryContacts] = useState([]);
  const [pickContact, setPickContact] = useState("");
  useEffect(() => { api.listContacts().then((r) => setDirectoryContacts(Array.isArray(r) ? r : [])).catch(() => setDirectoryContacts([])); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (i, k, v) => setForm((f) => ({ ...f, contacts: f.contacts.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const addDirectoryContact = () => {
    if (!pickContact) return;
    const c = directoryContacts.find((x) => x.id === pickContact);
    if (!c) return;
    if (form.contacts.some((x) => x.contact_id === c.id || (c.email && (x.email || "").toLowerCase() === c.email.toLowerCase()))) {
      toast.info("Contatto già associato al cantiere");
      return;
    }
    set("contacts", [...form.contacts, {
      contact_id: c.id,
      name: c.name || "",
      email: c.email || "",
      role: (c.site_roles || {})[site?.id] || c.role || "",
      phone: c.phone || "",
      company: c.company || "",
    }]);
    setPickContact("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Inserisci il nome del cantiere");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        street: form.street,
        house_number: form.house_number,
        postal_code: form.postal_code,
        city: form.city,
        province: form.province,
        country: form.country || "Italia",
        client_name: form.client_name,
        status: form.status,
        start_date: form.start_date || null, end_date: form.end_date || null, notes: form.notes,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        contacts: form.contacts.filter((c) => c.name || c.email),
      };
      const saved = site ? await api.updateSite(site.id, payload) : await api.createSite(payload);
      toast.success(site ? "Cantiere aggiornato" : "Cantiere creato");
      onSaved(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const input = (k, label, props = {}) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`site-form-${k}`} {...props}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="site-form-modal" className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-lg">{site ? "Modifica cantiere" : "Nuovo cantiere"}</h3>
          <button type="button" onClick={onClose} data-testid="site-form-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">{input("name", "Nome cantiere *", { placeholder: "es. Ristrutturazione Via Roma 12", autoFocus: true })}</div>
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[1fr_110px] gap-3">
            {input("street", "Via / Piazza", { placeholder: "es. Via del Fornetto" })}
            {input("house_number", "Civico", { placeholder: "12/A" })}
          </div>
          {input("postal_code", "CAP", { placeholder: "00146", inputMode: "numeric", maxLength: 5 })}
          {input("city", "Comune / Città", { placeholder: "Roma" })}
          {input("province", "Provincia", { placeholder: "RM", maxLength: 2 })}
          {input("country", "Nazione", { placeholder: "Italia" })}
          <div className="sm:col-span-2">{input("client_name", "Committente")}</div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Stato</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="site-form-status" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
              {Object.entries(SITE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {input("keywords", "Parole chiave per le email (separate da virgola)", { placeholder: "es. Via Roma, Rossi, pratica 123" })}
          {input("start_date", "Data inizio", { type: "date" })}
          {input("end_date", "Data fine prevista", { type: "date" })}
        </div>
        <div>
          <div className="flex flex-col gap-2 mb-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-medium text-slate-600">Referenti del cantiere</label>
              <button type="button" onClick={() => set("contacts", [...form.contacts, { contact_id: "", name: "", email: "", role: "", phone: "", company: "" }])} data-testid="site-form-add-contact" className="text-xs text-orange-600 font-semibold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Nuovo</button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <select value={pickContact} onChange={(e) => setPickContact(e.target.value)} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                <option value="">Seleziona un contatto dalla rubrica…</option>
                {directoryContacts.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}{c.company ? " · " + c.company : ""}{(c.sites || []).length ? " · già in " + c.sites.length + " cantiere/i" : ""}</option>)}
              </select>
              <button type="button" onClick={addDirectoryContact} disabled={!pickContact} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold disabled:opacity-50">Collega</button>
            </div>
            <div className="text-[11px] text-slate-400">Le parole chiave hanno priorità sui referenti nello smistamento email. Lo stesso contatto può essere collegato a più cantieri.</div>
          </div>
          <div className="space-y-2">
            {form.contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_130px_1fr_auto] gap-2 items-center" data-testid={`contact-row-${i}`}>
                <input value={c.name || ""} onChange={(e) => setContact(i, "name", e.target.value)} placeholder="Nome" data-testid={`site-contact-name-${i}`} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                <input value={c.role || ""} onChange={(e) => setContact(i, "role", e.target.value)} placeholder="Ruolo (DL, RUP, fornitore…)" data-testid={`site-contact-role-${i}`} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                <input value={c.company || ""} onChange={(e) => setContact(i, "company", e.target.value)} placeholder="Società" data-testid={`site-contact-company-${i}`} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                <input value={c.phone || ""} onChange={(e) => setContact(i, "phone", e.target.value)} placeholder="Telefono" type="tel" data-testid={`site-contact-phone-${i}`} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                <input value={c.email || ""} onChange={(e) => setContact(i, "email", e.target.value)} placeholder="Email" type="email" data-testid={`site-contact-email-${i}`} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                <button type="button" onClick={() => set("contacts", form.contacts.filter((_, j) => j !== i))} className="p-2 text-red-500 hover:bg-red-50 rounded-lg justify-self-start"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Note</label>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="site-form-notes" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600">Annulla</button>
          <button type="submit" disabled={saving} data-testid="site-form-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {site ? "Salva" : "Crea cantiere"}
          </button>
        </div>
      </form>
    </div>
  );
};
